import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { TranslocoModule } from '@ngneat/transloco';
import { ButtonModule } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { MessagesModule } from 'primeng/messages';

import { EventType, OrgDataSchema } from '@pwp-common';

import { NotInListValidator } from '../../../../common/validators/not-in-list-validator';
import { OrgDataService } from '../../../../services/orgs/org-data/org-data.service';
import { FormFieldHelpComponent } from '../../../form/form-field-help/form-field-help.component';

import { CreateEventTypeDialogData } from './interfaces';

@Component({
  selector: 'app-create-event-type-dialog',
  standalone: true,
  imports: [
    ButtonModule,
    CommonModule,
    FormFieldHelpComponent,
    InputTextModule,
    MessagesModule,
    ReactiveFormsModule,
    TranslocoModule,
  ],
  templateUrl: './create-event-type-dialog.component.html',
  styleUrls: ['./create-event-type-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateEventTypeDialogComponent {
  private readonly dialogRef = inject(DynamicDialogRef);

  private readonly dialogConfig = inject(DynamicDialogConfig);

  private readonly formBuilder = inject(UntypedFormBuilder);

  private readonly orgDataService = inject(OrgDataService);

  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  private readonly dialogData = this.dialogConfig.data as CreateEventTypeDialogData;

  private readonly orgData = this.dialogData.orgData;

  private readonly existingEventNames = this.orgData.getEventTypes().map((eventType) => eventType.getDisplayName());

  public readonly displayNameControlName = 'displayName';

  public readonly maxEventNameLength = 80;

  public readonly form = this.formBuilder.group({
    [this.displayNameControlName]: [
      '',
      [
        Validators.required,
        Validators.maxLength(this.maxEventNameLength),
        NotInListValidator.notInListValidator(this.existingEventNames),
      ],
    ],
  });

  public isSaving = false;

  public saveFailed = false;

  public get getDisplayNameControl(): UntypedFormControl {
    return this.form.get(this.displayNameControlName) as UntypedFormControl;
  }

  public isValid(): boolean {
    return this.form.valid && !this.isDisplayNameBlank;
  }

  private get isDisplayNameBlank(): boolean {
    return `${this.getDisplayNameControl.value ?? ''}`.trim().length === 0;
  }

  public close(): void {
    this.dialogRef.close(false);
  }

  public async save(): Promise<void> {
    this.saveFailed = false;
    this.form.markAllAsTouched();

    if (!this.isValid()) {
      return;
    }

    const eventType = this.getEventTypeToSave();
    const eventTypes = [...this.orgData.getEventTypes(), eventType];

    this.isSaving = true;

    try {
      await this.orgDataService.updateDocFields({
        docId: this.orgData.getId(),
        data: { [OrgDataSchema.eventTypes]: eventTypes.map((event) => event.serialize()) },
      });
      this.dialogRef.close(eventType);
    } catch {
      this.saveFailed = true;
    } finally {
      this.isSaving = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  private getEventTypeToSave(): EventType {
    return new EventType({
      internalName: this.orgDataService.createId(),
      displayName: this.getDisplayNameControl.value.trim(),
    });
  }
}
